/**
 * The PWA side effects that belong to the app chrome (T52): the offline-queue drain and
 * its toast. The deferred install event lives at module scope (`install-prompt-store.ts`)
 * and, since T890, the service worker registration lives in `app/root.tsx` — both because
 * this provider stands down on the player and they must not.
 *
 * Mounted once from `app/root.tsx`. Renders nothing until there is something to say.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toast } from "~/components/ui";
import { SYNC_TAG, drain, size } from "~/lib/pwa/offline-queue";
import { useAnalytics } from "~/components/analytics/AnalyticsProvider";
import { PWA_EVENTS } from "~/lib/analytics/events";
import { requestBackgroundSync } from "~/lib/pwa/register-sw";

/**
 * T393 removed both props. They existed only to veto the floating install card
 * (`inOnboarding`, `onMemberSurface`), and that card is gone: the invitation now lives
 * inside the dashboard grid, which is a member surface by construction and is never
 * reachable mid-onboarding. What is left here is the offline-queue drain, which does not
 * care where she is. T890 removed the update banner: a new version now arrives on her next
 * in-app click (`register-sw.ts`), with nothing to accept.
 */
export function PwaProvider() {
  const { t } = useTranslation("pwa");
  const { capture } = useAnalytics();
  const [toast, setToast] = useState<string | null>(null);

  // ─── Install prompt capture ────────────────────────────────────────────────
  // Deliberately absent. Both listeners live at module scope in
  // `install-prompt-store.ts`: registering them here meant (a) they died with the
  // component on every player entry, and (b) they were installed after hydration, so an
  // event fired earlier in the page load was lost outright.

  // ─── Offline queue ─────────────────────────────────────────────────────────
  // The window-side drain is the mechanism, not the fallback: iOS Safari has no
  // Background Sync and iOS is a first-class target. `drain` is single-flight, so an
  // `online` event and a Sync firing together cannot replay the same write twice.
  useEffect(() => {
    let cancelled = false;

    async function flush() {
      if (await size().catch(() => 0)) {
        const result = await drain().catch(() => null);
        if (!cancelled && result && result.sent > 0) {
          setToast(t("offline_synced"));
          // T516 — §7's sibling of `offline_queued`. Reachable for the first time now
          // that the queue has a producer; before this ticket `size()` was always 0.
          capture(PWA_EVENTS.offlineSynced, { sent: result.sent, dropped: result.dropped });
        }
      }
    }

    function onOnline() {
      void requestBackgroundSync(SYNC_TAG);
      void flush();
    }

    function onMessage(event: MessageEvent) {
      const drained = event.data as { type?: string; sent?: number; dropped?: number } | null;
      if (drained?.type === "OFFLINE_QUEUE_DRAINED") {
        setToast(t("offline_synced"));
        capture(PWA_EVENTS.offlineSynced, {
          sent: drained.sent ?? 0,
          dropped: drained.dropped ?? 0,
        });
      }
    }

    window.addEventListener("online", onOnline);
    navigator.serviceWorker?.addEventListener("message", onMessage);
    if (navigator.onLine) void flush();

    return () => {
      cancelled = true;
      window.removeEventListener("online", onOnline);
      navigator.serviceWorker?.removeEventListener("message", onMessage);
    };
  }, [t, capture]);

  useEffect(() => {
    if (toast === null) return;
    // The delivered ephemeral toast fades after 3 s (`banners.jsx`, «TOAST EFÍMERO»).
    const id = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(id);
  }, [toast]);

  if (toast === null) return null;

  // `onLight`: the provider sits in the root shell over cream member surfaces; the default
  // pill is DS §11's glass for dark stages and its ink vanished there (design rubric, T890 QA).
  return <Toast message={toast} visible variant="success" onLight />;
}
